import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { API, useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Landmark, MapPin, Building2, Users, BedDouble, Cross, Calendar, MessageSquare, ArrowLeft, ChevronRight, LogOut, UsersRound, AlertTriangle } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { CATEGORY_LABEL, ROLE_LABEL, SERVICE_LABEL, HOSPITAL_STATUS_LABEL, label } from "@/lib/labels";
import { Kpi, Section, Empty, Pill, downloadFile } from "@/components/services/Shared";
import { AlertCard } from "@/pages/dashboard/EpidemicAlerts";

function Shell({ title, subtitle, back, children }) {
  const { user, logout } = useApp();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="ministry-explore">
      <header className="bg-[#1E4631] text-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Link to={back} className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 grid place-items-center transition" data-testid="explore-back"><ArrowLeft className="w-5 h-5" /></Link>
            <div className="w-11 h-11 rounded-xl bg-[#D96C4A] grid place-items-center"><Landmark className="w-6 h-6" /></div>
            <div>
              <div className="font-[Outfit] font-bold text-lg leading-tight">{title}</div>
              <div className="text-xs text-white/70">{subtitle} · Accès lecture seule</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right text-xs"><div className="font-semibold">{user?.name}</div><div className="text-white/60">{user?.email}</div></div>
            <Button size="sm" variant="outline" className="bg-white/5 text-white border-white/20 hover:bg-white/10 hover:text-white" onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn"><LogOut className="w-4 h-4 mr-1.5" /> Déconnexion</Button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">{children}</main>
    </div>
  );
}

export function ProvinceList() {
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/ministry/provinces/overview`).then((r) => setData(r.data)).catch(() => toast.error("Chargement impossible")); }, []);
  if (!data) return <div className="text-sm text-[#5C6B61]">Chargement des provinces…</div>;
  const withData = data.provinces.filter((p) => p.hospitals_total > 0);
  const empty = data.provinces.filter((p) => p.hospitals_total === 0);
  return (
    <div data-testid="province-list">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {withData.map((p) => (
          <Link key={p.province} to={`/ministry/provinces/${encodeURIComponent(p.province)}`} data-testid={`province-${p.province}`}
            className="group bg-white rounded-xl border border-[#E5EBE7] p-4 hover:border-[#1E4631] hover:shadow-md transition-all">
            <div className="flex items-center justify-between">
              <div className="font-[Outfit] font-bold text-[#1E4631] inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-[#D96C4A]" />{p.province}</div>
              <ChevronRight className="w-4 h-4 text-[#5C6B61] group-hover:translate-x-0.5 transition" />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <div><div className="text-lg font-bold">{p.hospitals_total}</div><div className="text-[10px] uppercase text-[#5C6B61]">Hôpitaux</div></div>
              <div><div className="text-lg font-bold">{p.patients}</div><div className="text-[10px] uppercase text-[#5C6B61]">Patients</div></div>
              <div><div className="text-lg font-bold text-[#C62828]">{p.deceased}</div><div className="text-[10px] uppercase text-[#5C6B61]">Décès</div></div>
            </div>
            <div className="mt-2 text-xs text-[#5C6B61]">{p.hospitals_active} actif(s) · {p.hospitalized} hospitalisé(s) · {p.appointments} RDV</div>
          </Link>
        ))}
      </div>
      {empty.length > 0 && (
        <div className="mt-4 text-xs text-[#5C6B61]">
          <span className="font-semibold">Provinces sans hôpital partenaire ({empty.length}) :</span> {empty.map((p) => p.province).join(" · ")}
          {data.hospitals_without_province > 0 && <div className="mt-1 italic">{data.hospitals_without_province} hôpital(aux) sans province renseignée (non affichés).</div>}
        </div>
      )}
    </div>
  );
}

export function ProvinceDetail() {
  const { province } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/ministry/provinces/${encodeURIComponent(province)}/hospitals`).then((r) => setData(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Chargement impossible")); }, [province]);
  return (
    <Shell title={`Province · ${province}`} subtitle="Hôpitaux partenaires et enregistrements" back="/ministry">
      {!data ? <div className="text-[#5C6B61]">Chargement…</div> : (<>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Kpi label="Hôpitaux" value={data.hospitals.length} icon={Building2} testid="prov-kpi-hospitals" />
          <Kpi label="Patients" value={data.totals.patients} icon={Users} color="#7A8471" testid="prov-kpi-patients" />
          <Kpi label="Hospitalisés" value={data.totals.hospitalized} icon={BedDouble} color="#D96C4A" />
          <Kpi label="Décès" value={data.totals.deceased} icon={Cross} color="#C62828" />
          <Kpi label="Rendez-vous" value={data.totals.appointments} icon={Calendar} />
        </div>
        <Section title={`Hôpitaux de la province (${data.hospitals.length})`} icon={Building2} testid="province-hospitals">
          {data.hospitals.length === 0 ? <Empty>Aucun hôpital partenaire dans cette province.</Empty> : (
            <div className="divide-y divide-[#E5EBE7]">
              {data.hospitals.map((h) => (
                <Link key={h.id} to={`/ministry/hospitals/${h.id}`} className="py-3 flex items-center gap-4 flex-wrap hover:bg-[#FDFBF7] -mx-2 px-2 rounded-lg transition" data-testid={`hospital-row-${h.id}`}>
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-semibold text-[#1E4631]">{h.name}</div>
                    <div className="text-xs text-[#5C6B61]">{[h.commune, h.ville || h.city].filter(Boolean).join(", ")}</div>
                  </div>
                  <Pill color={h.status === "active" ? "#2E7D32" : h.status === "pending" ? "#E3A72F" : "#C62828"}>{label(HOSPITAL_STATUS_LABEL, h.status)}</Pill>
                  <div className="grid grid-cols-5 gap-4 text-center text-sm">
                    {[["Patients", h.counts.patients], ["Hospit.", h.counts.hospitalized], ["Décès", h.counts.deceased], ["RDV", h.counts.appointments], ["Staff", h.counts.staff]].map(([l, v]) => (
                      <div key={l}><div className="font-bold">{v}</div><div className="text-[10px] uppercase text-[#5C6B61]">{l}</div></div>
                    ))}
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#5C6B61]" />
                </Link>
              ))}
            </div>
          )}
        </Section>
      </>)}
    </Shell>
  );
}

export function HospitalStats() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/ministry/hospitals/${id}/stats`).then((r) => setD(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Chargement impossible")); }, [id]);
  const toChart = (obj, map) => Object.entries(obj || {}).map(([k, v]) => ({ name: map[k] || k, value: v }));
  return (
    <Shell title={d?.hospital?.name || "Hôpital"} subtitle={d ? `${d.hospital.province || "—"} · ${d.hospital.ville || d.hospital.city}` : ""} back={d?.hospital?.province ? `/ministry/provinces/${encodeURIComponent(d.hospital.province)}` : "/ministry"}>
      {!d ? <div className="text-[#5C6B61]">Chargement…</div> : (<>
        <div className="p-3 bg-white rounded-xl border-l-4 border-[#1E4631] text-xs text-[#5C6B61]">Enregistrements agrégés — aucune donnée nominative n'est exposée (secret médical). Statut : <strong>{label(HOSPITAL_STATUS_LABEL, d.hospital.status)}</strong>.</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="hospital-kpis">
          <Kpi label="Patients" value={d.counts.patients} icon={Users} testid="h-kpi-patients" />
          <Kpi label="Hospitalisés" value={d.counts.hospitalized} icon={BedDouble} color="#D96C4A" />
          <Kpi label="Décès" value={d.counts.deceased} icon={Cross} color="#C62828" />
          <Kpi label="Rendez-vous" value={d.counts.appointments} icon={Calendar} />
          <Kpi label="SMS envoyés" value={d.counts.sms} icon={MessageSquare} color="#7A8471" />
          <Kpi label="Personnel" value={d.counts.staff} icon={UsersRound} color="#2E5C7E" />
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <Section title="Patients par catégorie">
            <ResponsiveContainer width="100%" height={220}><BarChart data={toChart(d.by_category, CATEGORY_LABEL)}><CartesianGrid strokeDasharray="3 3" stroke="#E5EBE7" /><XAxis dataKey="name" fontSize={11} /><YAxis fontSize={11} allowDecimals={false} /><Tooltip /><Bar dataKey="value" name="Patients" fill="#1E4631" radius={[6, 6, 0, 0]} /></BarChart></ResponsiveContainer>
          </Section>
          <Section title="Décès par catégorie">
            <ResponsiveContainer width="100%" height={220}><BarChart data={toChart(d.deaths_by_category, CATEGORY_LABEL)}><CartesianGrid strokeDasharray="3 3" stroke="#E5EBE7" /><XAxis dataKey="name" fontSize={11} /><YAxis fontSize={11} allowDecimals={false} /><Tooltip /><Bar dataKey="value" name="Décès" fill="#C62828" radius={[6, 6, 0, 0]} /></BarChart></ResponsiveContainer>
          </Section>
          <Section title="Personnel par rôle">
            <div className="divide-y divide-[#E5EBE7] text-sm">{Object.entries(d.staff_by_role).map(([r, n]) => <div key={r} className="py-2 flex justify-between"><span>{ROLE_LABEL[r] || r}</span><strong>{n}</strong></div>)}</div>
          </Section>
          <Section title="Patients par service">
            <div className="divide-y divide-[#E5EBE7] text-sm">{Object.entries(d.by_service).map(([sv, n]) => <div key={sv} className="py-2 flex justify-between"><span>{SERVICE_LABEL[sv] || sv}</span><strong>{n}</strong></div>)}</div>
          </Section>
          <Section title="Principales causes de décès">
            {d.death_causes.length === 0 ? <Empty>Aucun décès enregistré.</Empty> : <div className="divide-y divide-[#E5EBE7] text-sm">{d.death_causes.map((c) => <div key={c.cause} className="py-2 flex justify-between"><span>{c.cause}</span><strong className="text-[#C62828]">{c.count}</strong></div>)}</div>}
          </Section>
          <Section title="Alertes épidémiques récentes" icon={AlertTriangle}>
            {d.epidemic_alerts.length === 0 ? <Empty>Aucune alerte déclarée par cet hôpital.</Empty> : <div className="divide-y divide-[#E5EBE7] text-sm">{d.epidemic_alerts.map((a) => <div key={a.id} className="py-2 flex justify-between gap-2"><span>{a.disease} · {a.cases} cas · {a.deaths} décès</span><Pill color={a.status === "open" ? "#C62828" : a.status === "closed" ? "#2E7D32" : "#E3A72F"}>{a.status}</Pill></div>)}</div>}
          </Section>
        </div>
      </>)}
    </Shell>
  );
}

export function MinistryAlertsPanel() {
  const [data, setData] = useState(null);
  const load = () => axios.get(`${API}/epidemic-alerts`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const ack = async (a) => {
    const note = window.prompt("Note de prise en compte (équipe déployée, investigation…) :", "Alerte reçue — investigation lancée par la DPS");
    if (note === null) return;
    try { await axios.patch(`${API}/epidemic-alerts/${a.id}/acknowledge`, { note }); toast.success("Alerte prise en compte"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  if (!data) return null;
  const active = data.alerts.filter((a) => a.status !== "closed");
  return (
    <Section title={`Alertes épidémiques actives (${active.length})`} icon={AlertTriangle} testid="ministry-alerts"
      action={<Button size="sm" variant="outline" onClick={() => downloadFile(`${API}/dhis2/ministry/alerts-export`, "SIMR_alertes.csv").then(() => toast.success("Export téléchargé")).catch(() => toast.error("Export impossible"))} data-testid="ministry-alerts-export">Exporter SIMR (CSV)</Button>}>
      {active.length === 0 ? <Empty>Aucune alerte épidémique en cours sur le territoire couvert.</Empty> : (
        <div className="divide-y divide-[#E5EBE7]">
          {active.map((a) => <AlertCard key={a.id} a={a} actions={a.status === "open" && <Button size="sm" className="bg-[#1E4631] text-white" onClick={() => ack(a)} data-testid={`ack-${a.id}`}>Accuser réception</Button>} />)}
        </div>
      )}
    </Section>
  );
}
